import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Grid,
  CircularProgress,
} from '@mui/material'
import { Print, ArrowBack } from '@mui/icons-material'
import { Button, DocumentQRCode, PrintStyles } from '@components/common'
import { useDocumentRegistry } from '@hooks/useDocumentRegistry'
import { toast } from 'react-toastify'
import api from '@services/api'
import { useAuth } from '@context/AuthContext'
import { formatDate, formatDateTime } from '@utils/dateUtils'

const JobCardPrint = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [jobCard, setJobCard] = useState(null)
  const [loading, setLoading] = useState(true)
  const { registerDocument, verificationCode, loading: registering } = useDocumentRegistry()

  useEffect(() => {
    const fetchJobCard = async () => {
      try {
        const response = await api.get(`/maintenance/job-cards/${id}/print_preview/`)
        setJobCard(response.data)
      } catch (error) {
        console.error('Error fetching job card:', error)
        toast.error('Failed to load job card')
        navigate('/maintenance/job-cards')
      } finally {
        setLoading(false)
      }
    }
    fetchJobCard()
  }, [id, navigate])

  // Set document title for meaningful PDF filename
  useEffect(() => {
    if (jobCard) {
      document.title = `Job Card - ${jobCard.job_card_number} - UCI CMMS`
    }
    return () => {
      document.title = 'UCI CMMS'
    }
  }, [jobCard])

  // Register document when job card is loaded
  useEffect(() => {
    if (jobCard && !verificationCode) {
      registerDocument('job_card', jobCard.job_card_number, parseInt(id), jobCard)
        .catch((err) => console.error('Failed to register document:', err))
    }
  }, [jobCard, id, verificationCode, registerDocument])

  const handlePrint = async () => {
    // Ensure document is registered before printing
    if (!verificationCode && jobCard) {
      try {
        await registerDocument('job_card', jobCard.job_card_number, parseInt(id), jobCard)
      } catch (err) {
        console.error('Registration failed:', err)
      }
    }
    window.print()
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!jobCard) {
    return null
  }

  return (
    <>
      {/* Print Controls - Hidden when printing */}
      <Box className="print-controls no-print" sx={{ p: 2, display: 'flex', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          variant="outlined"
          onClick={() => navigate('/maintenance/job-cards')}
        >
          Back
        </Button>
        <Button
          startIcon={<Print />}
          onClick={handlePrint}
          disabled={registering}
        >
          {registering ? 'Preparing...' : 'Print'}
        </Button>
      </Box>

      {/* Printable Content */}
      <Box className="print-content" sx={{ bgcolor: 'white' }}>
        {/* Header with UCI Logo */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          {/* Logo centered on first line */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <img
              src="/images/uci-logo.png"
              alt="UCI Logo"
              style={{ height: '70px' }}
            />
          </Box>
          {/* System name prominently displayed */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: '#1B5E7A',
              letterSpacing: '0.5px',
              mb: 2
            }}
          >
            Computerized Maintenance Management System
          </Typography>
          {/* Document title */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', textDecoration: 'underline' }}>
            MAINTENANCE JOB CARD
          </Typography>
        </Box>

        {/* Job Card Number */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="body1">
            <strong>Job Card #:</strong> {jobCard.job_card_number}
          </Typography>
          <Typography variant="body1">
            <strong>Work Order #:</strong> {jobCard.work_order_number}
          </Typography>
          <Typography variant="body1">
            <strong>Date:</strong> {formatDate(jobCard.generated_date) || '-'}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Service Request Reference (UCI Form) */}
        {jobCard.service_request_ref && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Service Request Form Ref:</strong> {jobCard.service_request_ref}
          </Typography>
        )}

        {/* Work Type Section (UCI Form) */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          TYPE OF WORK
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
          {[
            { value: 'corrective', label: 'CM/Repair' },
            { value: 'preventive', label: 'PM' },
            { value: 'setup_installation', label: 'Set-up/Installation' },
            { value: 'user_training', label: 'User Training' },
            { value: 'other', label: 'Other' },
          ].map((type) => (
            <Box key={type.value} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  border: '1px solid black',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                }}
              >
                {jobCard.work_type === type.value ? '✓' : ''}
              </Box>
              <Typography variant="body2">{type.label}</Typography>
            </Box>
          ))}
        </Box>

        {/* Asset Information */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          EQUIPMENT INFORMATION
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Asset Name</TableCell>
                <TableCell sx={{ width: '25%' }}>{jobCard.asset_name || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Asset Tag</TableCell>
                <TableCell sx={{ width: '25%' }}>{jobCard.asset_tag || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Model Number</TableCell>
                <TableCell>{jobCard.asset_model_number || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Serial Number</TableCell>
                <TableCell>{jobCard.asset_serial_number || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Engraved No.</TableCell>
                <TableCell>{jobCard.asset_engraved_no || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Sticker Grade</TableCell>
                <TableCell>{jobCard.equipment_sticker_grade_display || jobCard.equipment_sticker_grade || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
                <TableCell>{jobCard.asset_location || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Section/Unit</TableCell>
                <TableCell>{jobCard.department_name || '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Work Order Information */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          WORK ORDER DETAILS
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Title</TableCell>
                <TableCell colSpan={3}>{jobCard.work_order_title || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                <TableCell>{jobCard.work_order_category || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Priority</TableCell>
                <TableCell>{jobCard.work_order_priority || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Assigned To</TableCell>
                <TableCell>{jobCard.assigned_to_name || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Hours Worked</TableCell>
                <TableCell>{jobCard.actual_hours || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Start Time</TableCell>
                <TableCell>{formatDateTime(jobCard.start_time) || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Completion Time</TableCell>
                <TableCell>{formatDateTime(jobCard.completion_time) || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Time Spent on Service</TableCell>
                <TableCell colSpan={3}>{jobCard.time_spent_on_service || '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Problem Description */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          PROBLEM DESCRIPTION
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 3, minHeight: 60 }}>
          <Typography variant="body2">
            {jobCard.problem_description || jobCard.work_order_description || 'No description provided'}
          </Typography>
        </Paper>

        {/* Resolution */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          WORK PERFORMED / RESOLUTION
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 3, minHeight: 60 }}>
          <Typography variant="body2">
            {jobCard.resolution_description || 'No resolution description provided'}
          </Typography>
        </Paper>

        {/* Extra Work Needed (UCI Form) */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          EXTRA WORK TO BE DONE
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 3, minHeight: 60 }}>
          <Typography variant="body2">
            {jobCard.extra_work_needed || 'None'}
          </Typography>
        </Paper>

        {/* Parts Used */}
        {jobCard.parts_used && jobCard.parts_used.length > 0 && (
          <>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              PARTS / MATERIALS USED
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Part Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Part #</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Qty</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Unit Cost</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobCard.parts_used.map((part, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{part.part_name || part.spare_part_name}</TableCell>
                      <TableCell>{part.part_number || '-'}</TableCell>
                      <TableCell align="center">{part.quantity}</TableCell>
                      <TableCell align="right">{part.unit_cost?.toLocaleString() || '-'}</TableCell>
                      <TableCell align="right">{part.total_cost?.toLocaleString() || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Labor Records */}
        {jobCard.labor_records && jobCard.labor_records.length > 0 && (
          <>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              LABOR RECORDS
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Technician</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Task Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Hours</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Rate</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobCard.labor_records.map((labor, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{labor.technician_name}</TableCell>
                      <TableCell>{labor.task_description || '-'}</TableCell>
                      <TableCell align="center">{labor.hours_worked}</TableCell>
                      <TableCell align="right">{labor.hourly_rate?.toLocaleString() || '-'}</TableCell>
                      <TableCell align="right">{labor.total_cost?.toLocaleString() || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Signatures Section */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          SIGNATURES
        </Typography>

        {/* Technician Signatures (UCI Form - multiple technicians) */}
        {jobCard.technician_signatures && jobCard.technician_signatures.length > 0 ? (
          jobCard.technician_signatures.map((sig, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-end', gap: 4, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="body2"><strong>Technician/Engineer:</strong></Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2">Sign:</Typography>
                <Box sx={{ borderBottom: '1px solid black', minWidth: 80, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', fontWeight: 'bold' }}>{sig.initials || ''}</Typography>
                </Box>
              </Box>
              <Typography variant="body2">Name: {sig.name || '_________'}</Typography>
              <Typography variant="body2">Date: {formatDate(sig.date) || '_________'}</Typography>
            </Box>
          ))
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 4, flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="body2"><strong>Technician/Engineer:</strong></Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2">Sign:</Typography>
              <Box sx={{ borderBottom: '1px solid black', minWidth: 80, height: 24 }} />
            </Box>
            <Typography variant="body2">Name: {jobCard.assigned_to_name || '_________'}</Typography>
            <Typography variant="body2">Date: _________</Typography>
          </Box>
        )}

        {/* Section/Unit Head/Supervisor Acknowledgment */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 4, flexWrap: 'wrap' }}>
          <Typography variant="body2"><strong>Section/Unit Head/Supervisor:</strong></Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2">Sign:</Typography>
            <Box sx={{ borderBottom: '1px solid black', minWidth: 80, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {jobCard.signature ? (
                <Box component="img" src={jobCard.signature} sx={{ maxHeight: 22, objectFit: 'contain' }} />
              ) : (
                <Typography variant="body2" sx={{ fontStyle: 'italic', fontWeight: 'bold' }}>{jobCard.supervisor_initials || ''}</Typography>
              )}
            </Box>
          </Box>
          <Typography variant="body2">Name: {jobCard.acknowledged_by_name || jobCard.supervisor_name || '_________'}</Typography>
          <Typography variant="body2">Date: {formatDate(jobCard.acknowledged_at) || formatDate(jobCard.supervisor_date) || '_________'}</Typography>
          {jobCard.acknowledgment_notes && <Typography variant="body2"><strong>Acknowledgement Comment:</strong> {jobCard.acknowledgment_notes}</Typography>}
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Printed: {formatDateTime(new Date())}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Printed by: {(user?.first_name || user?.last_name) ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim() : user?.username || 'N/A'}
            </Typography>
          </Box>
          <DocumentQRCode
            verificationCode={verificationCode}
            documentType="Job Card"
            documentNumber={jobCard?.job_card_number || ''}
            size={80}
          />
        </Box>
      </Box>

      <PrintStyles orientation="portrait" />
    </>
  )
}

export default JobCardPrint
